import React, { useState, useRef, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Spinner, InputGroup, InputGroupText } from "reactstrap";
import CommonButton from "Components/Common/CommonButton";
import styles from "./VerifyLabTestModal.module.scss";

interface VerifyLabTestModalProps {
  isOpen: boolean;
  toggle: () => void;
  onVerify: (password: string, decision: "Pass" | "Fail") => void;
  isLoading?: boolean;
  title?: string;
  recordName?: string;
  isInvalid?: boolean;
}

const VerifyLabTestModal: React.FC<VerifyLabTestModalProps> = ({
  isOpen,
  toggle,
  onVerify,
  isLoading = false,
  title = "Verify Lab Test Record",
  recordName = "",
  isInvalid = false,
}) => {
  const [password, setPassword] = useState("");
  const [decision, setDecision] = useState<"Pass" | "Fail" | null>(null);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setDecision(null);
      setIsAcknowledged(false);
      setShowPassword(false);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && password.trim() !== "" && decision && isAcknowledged) {
      handleVerify();
    }
  };

  const handleVerify = () => {
    if (password.trim() !== "" && decision && isAcknowledged) {
      onVerify(password, decision);
    }
  };

  const isComplete = password.trim().length > 0 && decision !== null && isAcknowledged;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="fw-bold border-bottom p-3">
        <i className="ri-shield-keyhole-line me-2 text-primary fs-20 align-middle" />
        <span className="align-middle fs-18">{title}</span>
      </ModalHeader>

      <ModalBody className="py-4 pb-0">
        {/* Dummy hidden fields to trick password managers and browsers */}
        <div style={{ position: 'absolute', opacity: 0, height: 0, width: 0, overflow: 'hidden' }} aria-hidden="true">
          <input type="text" name={`fake_username_${Date.now()}`} tabIndex={-1} autoComplete="off" />
          <input type="password" name={`fake_password_${Date.now()}`} tabIndex={-1} autoComplete="off" />
        </div>

        {/* Record Description */}
        <div className="mb-3 px-4">
          <div className={styles.infoBanner}>
            <i className="ri-information-line fs-18"></i>
            <div className={styles.bannerText}>
              Please review the lab test record: <strong>{recordName || "Selected Record"}</strong>. 
              Verify and confirm your decision below.
            </div>
          </div>
        </div>

        {/* Verify Decision Section (Vertical Layout aligned to px-4) */}
        <div className="mb-4 px-4">
          <div className="text-start mb-2">
            <label className="form-label fw-bold text-dark mb-1">
              Verify Decision <span className="text-danger">*</span>
            </label>
          </div>
          <div className="d-flex gap-3">
            <CommonButton
              color={decision === "Pass" ? "success" : "light"}
              onClick={() => setDecision("Pass")}
              className={`flex-grow-1 py-2 px-3 fw-semibold text-center ${styles.buttonPass} ${decision === "Pass" ? styles.active : ""}`}
            >
              <i className={`${decision === "Pass" ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"} me-2 fs-16 align-middle`} />
              <span className="align-middle">Pass</span>
            </CommonButton>

            <CommonButton
              color={decision === "Fail" ? "danger" : "light"}
              onClick={() => setDecision("Fail")}
              className={`flex-grow-1 py-2 px-3 fw-semibold text-center ${styles.buttonFail} ${decision === "Fail" ? styles.active : ""}`}
            >
              <i className={`${decision === "Fail" ? "ri-close-circle-fill" : "ri-close-circle-line"} me-2 fs-16 align-middle`} />
              <span className="align-middle">Fail</span>
            </CommonButton>
          </div>
        </div>

        {/* Password Section (Vertical Layout aligned to px-4) */}
        <div className="mb-4 px-4">
          <div className="text-start mb-2">
            <label className="form-label fw-bold text-dark mb-1">
              Password <span className="text-danger">*</span>
            </label>
          </div>
          
          <InputGroup className="custom-input-group mb-2">
            <InputGroupText className={`${styles.passwordAddonLeft} ${isInvalid ? styles.invalid : ""}`}>
              <i className="ri-lock-2-line text-muted fs-16"></i>
            </InputGroupText>
            
            <input
              ref={inputRef}
              type={showPassword ? "text" : "password"}
              name="password"
              id="verify_password_field"
              className={`form-control ${isInvalid ? 'is-invalid' : ''} ${styles.passwordInput} ${isInvalid ? styles.invalid : ""}`}
              placeholder="Verify password"
              value={password}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
              readOnly
              onFocus={(e) => {
                e.target.readOnly = false;
              }}
            />
            
            <InputGroupText 
              onClick={() => setShowPassword(!showPassword)}
              className={`${styles.passwordAddonRight} ${isInvalid ? styles.invalid : ""}`}
            >
              <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} ${isInvalid ? 'text-danger' : 'text-primary'} fs-16`}></i>
            </InputGroupText>
          </InputGroup>

          <div className="d-flex align-items-center gap-2 mb-3">
            <i className="ri-checkbox-circle-line text-success fs-15"></i>
            <span className="text-muted fs-13">Your password is encrypted and secure.</span>
          </div>

          <div className="text-start mt-1 d-flex align-items-start gap-2">
            <input
              type="checkbox"
              id="signature_ack"
              checked={isAcknowledged}
              onChange={(e) => setIsAcknowledged(e.target.checked)}
              className="form-check-input mt-1"
              style={{ cursor: "pointer" }}
            />
            <label htmlFor="signature_ack" className="form-check-label text-dark fs-14" style={{ cursor: "pointer", userSelect: "none" }}>
              I acknowledge that my credentials will be used as an electronic signature for this action.
            </label>
          </div>

          {isInvalid && (
            <div className="text-danger mt-2 fs-13 fw-medium text-start">
              <i className="ri-error-warning-line me-1"></i>
              Invalid password. Please try again.
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <CommonButton 
          color="dark" 
          outline
          onClick={toggle} 
          disabled={isLoading}
        >
          Cancel
        </CommonButton>
        <CommonButton 
          color="primary"
          outline
          onClick={handleVerify} 
          disabled={!isComplete || isLoading}
        >
          {isLoading && <Spinner size="sm" className="me-2" />}
          Verify
        </CommonButton>
      </ModalFooter>
    </Modal>
  );
};

export default VerifyLabTestModal;
